/**
 * Shared cookie utilities for stage-prefixed cookie names.
 * Used by auth, toolkit, and website packages to ensure consistent cookie handling.
 */

export type CookieName = 'access_token' | 'refresh_token' | 'user_type';

/**
 * Get the current SST stage.
 * Throws if no stage environment variable is set to prevent silent production fallback.
 *
 * @throws Error if SST_STAGE and NEXT_PUBLIC_SST_STAGE are both undefined
 */
export function getStage(): string {
  const stage = process.env.SST_STAGE || process.env.NEXT_PUBLIC_SST_STAGE;
  if (!stage) {
    throw new Error('SST_STAGE or NEXT_PUBLIC_SST_STAGE must be set');
  }
  return stage;
}

/**
 * Get stage-prefixed cookie name.
 * This ensures complete cookie isolation between environments, preventing
 * cookie collisions when users access multiple stages (e.g., dev.toptoptime.com
 * and jordane.dev.toptoptime.com).
 *
 * Production uses unprefixed names for backward compatibility and cleaner cookies.
 *
 * Examples:
 * - production: access_token, refresh_token, user_type
 * - dev: dev_access_token, dev_refresh_token, dev_user_type
 * - jordane: jordane_access_token, jordane_refresh_token, jordane_user_type
 */
export function getCookieName(baseName: CookieName): string {
  const stage = getStage();

  // Production uses unprefixed names for backward compatibility
  if (stage === 'production') {
    return baseName;
  }

  return `${stage}_${baseName}`;
}
